import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaUser } from "react-icons/fa";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod/v4";
import { useUserStore } from "../stores/UserStore";
import { LoginFormSchema } from "../forms/LoginFormSchema";
import { login } from "../backend/api/auth";
import { useMutation } from "@tanstack/react-query";
import { LoginRequest, LoginResponse } from "../backend/types/api-types";
import toast from "react-hot-toast";
import { useLoginAndRegisterStore } from "../stores/LoginAndRegisterStore";
import i18n from "../i18n";
import LoginRegisterWithGoogle from "./LoginRegisterWithGoogle";
import { trackLogin } from "../utils/analytics";
import { PasswordInput } from "./PasswordInput";
import GearHeadings from "../ui/gearheadings";
import GearParagraph from "../ui/gearparagraph";
import { Tab } from "../ui/geartab";
import { GearTab } from "../ui/geartab";
import RegisterForm from "./RegisterForm";
import GearButton from "../ui/gearbutton";
import GearInput from "../ui/gearinput";

type LoginForm = z.infer<typeof LoginFormSchema>;

interface LoginButtonProps {
  currentUser: string | null;
  onLogin: (email: string | null) => void;
}

const LoginButton: React.FC<LoginButtonProps> = ({ currentUser, onLogin }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const { setUser } = useUserStore();
  const { openLogin, openRegister, isLoginOpen, close } =
    useLoginAndRegisterStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<LoginForm>({
    resolver: zodResolver(LoginFormSchema),
    mode: "onTouched",
  });

  const { mutate: loginMutation, isPending } = useMutation({
    mutationFn: (value: LoginRequest) => login(value, i18n.language),
    onSuccess: (data: LoginResponse) => {
      console.log(data);
      setUser({ ...data.user, access_token: data.access_token });

      // Track login in Google Analytics
      trackLogin("email", data.user.id);

      close();
      reset();
    },
    onError: (error: any) => {
      toast.error(error.response.data.message);
      reset({ password: "" });
    },
  });

  const onSubmit: SubmitHandler<LoginForm> = useCallback(
    async (value: LoginForm) => {
      loginMutation(value);
    },
    [],
  );

  return (
    <div className="z-[1000]">
      {currentUser ? (
        <div className="bg-white px-4 py-2 rounded-modal shadow-lg">
          <div className="mb-2">
            <span className="text-sm text-gray-600">
              {t("navigation.loggedInAs", "Logged in as")}:{" "}
            </span>
            <span className="text-sm">{currentUser}</span>
          </div>
          <button
            onClick={() => onLogin(null)}
            style={{ display: "flex" }}
            className=" text-red-600 hover:text-red-700 text-left header-button"
          >
            {t("navigation.logout")}
          </button>
        </div>
      ) : (
        <>
          <button
            onClick={() => openLogin()}
            className="items-center gap-2 text-white hidden md:flex"
          >
            <img
              src={"/icons/login.svg"}
              alt="People"
              className="ml-[-4px] object-contain"
            />
            <span className="text-[16px]">{t("navigation.login")}</span>
          </button>

          {isLoginOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1001] p-4">
              <div className="bg-paper border-modal max-h-[90vh] overflow-y-auto p-6 rounded-[25px] shadow-xl w-full relative max-w-[512px]">
                <button
                  onClick={() => close()}
                  className="absolute top-4 right-4"
                >
                  <img src="/icons/close.svg" alt="Close" className="w-4 h-4" />
                </button>
                <div className="flex flex-col justify-end items-center mb-4">
                  <img
                    src="/icons/logo_login.svg"
                    alt="Logo"
                    className="w-16 h-16"
                  />
                  <GearHeadings
                    variant="h3"
                    className="text-center text-white font-[600] mb-2"
                  >
                    {t("navigation.loginTitle")}
                  </GearHeadings>
                  <GearParagraph variant="medium" className="text-center mb-2">
                    {t("navigation.loginSubtitle")}
                  </GearParagraph>
                </div>
                <GearTab>
                  <Tab label={t("navigation.login")}>
                    <form
                      onSubmit={handleSubmit(onSubmit)}
                      className="space-y-4"
                    >
                      <GearInput
                        label={t("forms.email", "Email")}
                        type="email"
                        icon={
                          <img
                            src="/icons/mail.svg"
                            alt="Mail"
                            className="w-4 h-4"
                          />
                        }
                        {...register("email")}
                        placeholder={"your@email.com"}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-2xl focus:border-primary focus:outline-none transition-all"
                        required
                        error={
                          errors.email ? t(errors.email.message!) : undefined
                        }
                      />

                      <GearInput
                        label={t("forms.password", "Password")}
                        type="password"
                        icon={
                          <img
                            src="/icons/lock.svg"
                            alt="Lock"
                            className="w-4 h-4"
                          />
                        }
                        {...register("password")}
                        placeholder={"* * * * * * * *"}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-2xl focus:border-primary focus:outline-none transition-all"
                        required
                        error={
                          errors.password
                            ? t(errors.password.message!)
                            : undefined
                        }
                      />
                      <GearButton
                        variant="secondary"
                        size="medium"
                        type="submit"
                        className="w-full rounded-full"
                        disabled={!isValid || isPending}
                      >
                        {t("navigation.login", "Accedi")}
                      </GearButton>
                    </form>
                  </Tab>
                  <Tab label={t("forms.register")}>
                    <RegisterForm />
                  </Tab>
                </GearTab>
                <div className="flex flex-col px-1">
                  <div className="my-4 text-center flex items-center justify-center text-[#D1D5DC] relative">
                    <div className="flex-1 border-t border-solid border-[#DDD6FE] border-opacity-100 border-1 z-1" />
                    <span className="text-[12px] px-2 uppercase relative z-10">
                      {t("forms.or", "oppure")}
                    </span>
                    <div className="flex-1 border-t border-solid border-[#DDD6FE] border-opacity-100 border-1 z-1" />
                  </div>
                  <LoginRegisterWithGoogle type="login" />
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LoginButton;
